import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import LocalizedLink from '../components/common/LocalizedLink';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/common/SEO';
import {
  FiHeart, FiUsers, FiShield, FiStar,
  FiTrendingUp, FiAward, FiMapPin, FiSmartphone, FiCheckCircle, FiHome
} from 'react-icons/fi';

const AboutPage = () => {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const storyRef = useRef(null);
  const missionRef = useRef(null);
  const valuesRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const storyInView = useInView(storyRef, { once: true, margin: "-100px" });
  const missionInView = useInView(missionRef, { once: true, margin: "-100px" });
  const valuesInView = useInView(valuesRef, { once: true, margin: "-100px" });

  const stats = [
    { value: '2026', label: t('pages.founded'), color: 'text-primary-600' },
    { value: t('pages.uniqueStatValue'), label: t('pages.uniqueStat'), color: 'text-secondary-600' },
    { value: t('pages.alwaysThereValue'), label: t('pages.alwaysThere'), color: 'text-accent-600' },
  ];

  // Structured Data for About Page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": t('common.seo.about.title'),
    "description": t('common.seo.about.description'),
    "url": "https://petcarenote.com/#/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "Pet Care Note",
      "url": "https://petcarenote.com",
      "logo": "https://petcarenote.com/android-chrome-512x512.png",
      "foundingDate": "2026",
      "description": t('common.seo.about.description'),
      "sameAs": []
    }
  };

  return (
    <>
      <SEO 
        title={t('common.seo.about.title')}
        description={t('common.seo.about.description')}
        keywords={t('common.seo.about.keywords')}
        structuredData={structuredData}
      />
      <div className="pt-20 sm:pt-24 pb-16">
        {/* Hero Section */}
        <section ref={heroRef} className="bg-gradient-to-br from-primary-600 to-secondary-700 pt-16 pb-20 px-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 paw-pattern-bg opacity-10"></div>
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7 }}
            >
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <SafeIcon icon={FiHeart} className="text-3xl" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('pages.aboutUs')}</h1>
              <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto">
                {t('pages.aboutUsSubtitle')}
              </p>
            </motion.div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 50" className="fill-white">
              <path d="M0,25L80,28.3C160,32,320,38,480,35C640,32,800,20,960,17C1120,13,1280,20,1360,23.3L1440,25L1440,50L1360,50C1280,50,1120,50,960,50C800,50,640,50,480,50C320,50,160,50,80,50L0,50Z"></path>
            </svg>
          </div>
        </section>

        {/* Our Story Section */}
        <section ref={storyRef} className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0 }}
              animate={storyInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div>
                <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-800 text-sm font-medium mb-4 shadow-sm">
                  {t('pages.ourStory')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-800">{t('pages.howItAllBegan')}</h2>
                <div className="space-y-5 text-neutral-600 text-lg leading-relaxed">
                  <p>{t('pages.storyParagraph1')}</p>
                  <p>{t('pages.storyParagraph2')}</p>
                  <p>{t('pages.storyParagraph3')}</p>
                </div>

                {/* Story highlights */}
                <div className="mt-8 space-y-3">
                  {[
                    { icon: FiMapPin, key: 'storyHighlight1' },
                    { icon: FiSmartphone, key: 'storyHighlight2' },
                    { icon: FiCheckCircle, key: 'storyHighlight3' },
                  ].map(({ icon, key }) => (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={icon} className="text-sm text-primary-600" />
                      </div>
                      <span className="text-neutral-600 text-sm">{t(`pages.${key}`)}</span>
                    </div>
                  ))}
                </div>

                {/* Stats - 真實數據 */}
                <div className="mt-8 grid grid-cols-3 gap-4">
                  {stats.map(({ value, label, color }) => (
                    <div key={label} className="text-center p-3 rounded-xl bg-neutral-50 border border-neutral-100">
                      <div className={`text-xl font-bold ${color} leading-tight`}>{value}</div>
                      <div className="text-xs text-neutral-500 mt-1 leading-tight">{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Story images */}
              <div className="flex flex-col gap-4">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={storyInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative"
                >
                  {/* 主人與寵物告別、準備旅行的溫馨場景 */}
                  <img
                    src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                    alt="Pet owner saying goodbye to pet before travel - emotional moment showing the need for pet care management"
                    className="rounded-2xl shadow-lg w-full object-cover h-56"
                    loading="lazy"
                  />
                  <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2 shadow-sm">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiHeart} className="text-primary-500 text-sm" />
                      <span className="text-xs font-medium text-neutral-700">{t('pages.storyImageCaption1')}</span>
                    </div>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={storyInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
                  transition={{ duration: 0.6, delay: 0.35 }}
                  className="relative"
                >
                  {/* 貓咪在窗邊等待主人回家的溫馨場景 */}
                  <img
                    src="https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                    alt="Cat waiting at window for owner to return home - showcasing pet care and companionship"
                    className="rounded-2xl shadow-lg w-full object-cover h-44"
                    loading="lazy"
                  />
                  <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2 shadow-sm">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiHome} className="text-secondary-500 text-sm" />
                      <span className="text-xs font-medium text-neutral-700">{t('pages.storyImageCaption2')}</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Our Mission Section */}
        <section ref={missionRef} className="py-16 px-6 bg-gradient-to-br from-neutral-50 to-primary-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 35" className="fill-white rotate-180">
              <path d="M0,18L80,20C160,22,320,26,480,24C640,22,800,14,960,12C1120,10,1280,14,1360,16L1440,18L1440,35L1360,35C1280,35,1120,35,960,35C800,35,640,35,480,35C320,35,160,35,80,35L0,35Z"></path>
            </svg>
          </div>
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={missionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-secondary-100 text-secondary-800 text-sm font-medium mb-4 shadow-sm">
                {t('pages.ourMission')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">{t('pages.whyWeExist')}</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-secondary-600 to-primary-600 mx-auto mb-6"></div>
              <p className="text-neutral-600 mb-8 max-w-3xl mx-auto text-lg">{t('pages.missionStatement')}</p>
            </motion.div>
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              animate={missionInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <MissionCard icon={FiHeart} title={t('pages.betterCare')} description={t('pages.betterCareDesc')} color="bg-primary-500" />
              <MissionCard icon={FiShield} title={t('pages.peaceMind')} description={t('pages.peaceMindDesc')} color="bg-secondary-500" />
              <MissionCard icon={FiUsers} title={t('pages.buildCommunity')} description={t('pages.buildCommunityDesc')} color="bg-accent-500" />
            </motion.div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 35" className="fill-white">
              <path d="M0,18L80,20C160,22,320,26,480,24C640,22,800,14,960,12C1120,10,1280,14,1360,16L1440,18L1440,35L1360,35C1280,35,1120,35,960,35C800,35,640,35,480,35C320,35,160,35,80,35L0,35Z"></path>
            </svg>
          </div>
        </section>

        {/* Our Values Section */}
        <section ref={valuesRef} className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={valuesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-800 text-sm font-medium mb-4 shadow-sm">
                {t('pages.ourValues')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">{t('pages.whatDrivesUs')}</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-accent-600 to-secondary-600 mx-auto mb-6"></div>
              <p className="text-neutral-600 mb-8 max-w-3xl mx-auto text-lg">{t('pages.valuesDescription')}</p>
            </motion.div>
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              animate={valuesInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <ValueCard icon={FiStar} title={t('pages.excellence')} description={t('pages.excellenceDesc')} />
              <ValueCard icon={FiTrendingUp} title={t('pages.innovation')} description={t('pages.innovationDesc')} />
              <ValueCard icon={FiAward} title={t('pages.reliability')} description={t('pages.reliabilityDesc')} />
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 bg-gradient-to-br from-primary-600 to-secondary-700 text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 35" className="fill-white rotate-180">
              <path d="M0,18L80,20C160,22,320,26,480,24C640,22,800,14,960,12C1120,10,1280,14,1360,16L1440,18L1440,35L1360,35C1280,35,1120,35,960,35C800,35,640,35,480,35C320,35,160,35,80,35L0,35Z"></path>
            </svg>
          </div>
          <div className="max-w-7xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('pages.joinOurMission')}</h2>
              <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto">
                {t('pages.joinMissionDescription')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <LocalizedLink to="/dashboard" className="btn btn-accent btn-lg shadow-lg">
                  {t('pages.getStarted')}
                </LocalizedLink>
                <LocalizedLink to="/contact" className="btn btn-white btn-lg">
                  {t('navbar.contact')}
                </LocalizedLink>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

const MissionCard = ({ icon, title, description, color }) => (
  <motion.div
    className="bg-white rounded-xl shadow-smooth hover:shadow-card transition-all duration-300 border border-neutral-100 overflow-hidden"
    whileHover={{ y: -5 }}
    transition={{ duration: 0.2 }}
  >
    <div className={`${color} text-white p-6 text-center`}>
      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <SafeIcon icon={icon} className="text-3xl" />
      </div>
    </div>
    <div className="p-6">
      <h3 className="text-xl font-semibold mb-3 text-neutral-800">{title}</h3>
      <p className="text-neutral-600">{description}</p>
    </div>
  </motion.div>
);

const ValueCard = ({ icon, title, description }) => (
  <motion.div
    className="bg-white rounded-xl shadow-smooth hover:shadow-card transition-all duration-300 border border-neutral-100 p-6"
    whileHover={{ y: -5 }}
    transition={{ duration: 0.2 }}
  >
    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
      <SafeIcon icon={icon} className="text-xl text-primary-600" />
    </div>
    <h3 className="text-xl font-semibold mb-3 text-neutral-800">{title}</h3>
    <p className="text-neutral-600">{description}</p>
  </motion.div>
);

export default AboutPage;